import { createContext, useCallback, useContext, useMemo, ReactNode } from "react";
import { useAuth } from "./AuthHook";
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
  useSuspenseQuery,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import * as GomonTypes from "../types/gomonTypes";

interface GomonAPIContext {
  derivedData: {
    getSeries: QueryFactory<GomonTypes.GetSeriesRequest, GomonTypes.GetSeriesResponse>;
    getConsolidated: QueryFactory<GomonTypes.GetConsolidatedRequest, GomonTypes.GetConsolidatedResponse>;
    download: DownloadFactory<GomonTypes.DownloadRequest>;
    getRedGreen: QueryFactory<GomonTypes.RedGreenRequest, GomonTypes.RedGreenResponse[]>;
  };
  wells: {
    list: QueryFactory<GomonTypes.WellsListRequest, GomonTypes.WellsListResponse>;
    getBySiteId: QueryFactory<GomonTypes.WellsGetByIdRequest, GomonTypes.WellInternal[]>;
    getById: QueryFactory<GomonTypes.WellsGetByIdRequest, GomonTypes.WellsGetByIdResponse>;
    getDerived: QueryFactory<GomonTypes.WellsGetDerivedReq, GomonTypes.WellsGetDerivedResp>;
    edit: (body: GomonTypes.EditWellRequest) => Promise<GomonTypes.WellInternal>;
    create: (body: GomonTypes.CreateWellRequest) => Promise<GomonTypes.WellInternal>;
  };
  quantifiers: {
    getByWellId: QueryFactory<GomonTypes.WellsGetByIdRequest, GomonTypes.Quantifier[]>;
    getBySiteId: QueryFactory<GomonTypes.WellsGetByIdRequest, GomonTypes.Quantifier[]>;
    create: (body: GomonTypes.CreateQuantifierRequest) => Promise<GomonTypes.Quantifier>;
    edit: (body: GomonTypes.UpdateQuantifierRequest) => Promise<GomonTypes.Quantifier>;
    getAllByTCMDevUID: QueryFactory<GomonTypes.GetAllByTCMDevUIDRequest, GomonTypes.Quantifier[]>;
  };
  sites: {
    list: QueryFactory<GomonTypes.SitesListRequest, GomonTypes.SitesListResponse>;
    create: (body: GomonTypes.SitesCreateRequest) => Promise<GomonTypes.SitesCreateResponse>;
    update: (body: GomonTypes.SitesUpdateRequest) => Promise<GomonTypes.SitesUpdateResponse>;
    getByUserId: QueryFactory<GomonTypes.SitesGetByUserIdRequest, GomonTypes.SitesGetByUserIdResponse>;
    getById: QueryFactory<GomonTypes.SitesGetBySiteIDRequest, GomonTypes.Site>;
    getDatasetRevisions: QueryFactory<GomonTypes.ListDatasetRevisionsBySiteRequest, GomonTypes.DatasetRevision[]>;
  }
  getNSZD: QueryFactory<GomonTypes.NSZDGetBySiteRequest, GomonTypes.NSZDGetBySiteResponse>;
  getLatestPlumefutures: QueryFactory<GomonTypes.PlumeFuturesGetLatestImagesReq, GomonTypes.PlumeFuturesGetLatestImagesResp>;
  company: {
    index: QueryFactory<GomonTypes.CompaniesIndexRequest, GomonTypes.CompaniesIndexResponse>;
    show: QueryFactory<GomonTypes.CompaniesShowRequest, GomonTypes.CompaniesShowResponse>;
    listSites: QueryFactory<GomonTypes.CompaniesListSitesRequest, GomonTypes.CompaniesListSitesResponse>;
    create: (body: GomonTypes.CompaniesCreateRequest) => Promise<GomonTypes.CompaniesCreateResponse>;
  };
  users: {
    list: QueryFactory<GomonTypes.UsersListRequest, GomonTypes.UsersListResponse>;
    get: QueryFactory<GomonTypes.UsersGetRequest, GomonTypes.UsersGetResponse>;
    create: (body: GomonTypes.UsersCreateRequest) => Promise<GomonTypes.UsersCreateResponse>;
    updateUserSites: (body: GomonTypes.UpdateUserSitesRequest) => Promise<GomonTypes.UpdateUserSitesResponse>;
    listInternal: QueryFactory<GomonTypes.UsersListInternalRequest, GomonTypes.UsersListResponse>;
    setFieldCrew: (body: GomonTypes.SetFieldCrewRequest) => Promise<GomonTypes.SetFieldCrewResponse>;
  };
  plans: {
    getDeviceDetails: QueryFactory<GomonTypes.GetDeviceDetailsRequest, GomonTypes.GetDeviceDetailsResponse>;
    updateDevice: (body: GomonTypes.UpdateDeviceRequest) => Promise<GomonTypes.UpdateDeviceResponse>;
  };
  devices: {
    getByLabel: QueryFactory<GomonTypes.GetByTCMLabelRequest, GomonTypes.TCM>;
    find: QueryFactory<GomonTypes.FindTCMRequest, GomonTypes.TCMResponse>;
  };
  gateways: {
    list: QueryFactory<GomonTypes.GatewayList, GomonTypes.Gateway[]>;
    getNotes: QueryFactory<GomonTypes.GetGatewayNotes, GomonTypes.GatewayNotes[]>;
    getWisdmStatus: QueryFactory<GomonTypes.GetWisdmStatusRequest, GomonTypes.WisdmGatewayStatus>;
    create: (body: GomonTypes.Gateway) => Promise<GomonTypes.Gateway>;
    createNotes: (body: GomonTypes.GatewayNotes) => Promise<GomonTypes.GatewayNotes>;
    update: (body: GomonTypes.Gateway) => Promise<GomonTypes.Gateway>;
  },
  calibration: {
    list: QueryFactory<GomonTypes.GetCalibrationRequest, GomonTypes.CalibrationListResponse>;
    calibrateO2: QueryFactory<GomonTypes.CalibrateO2Request, GomonTypes.CalibrateO2Response>;
    saveO2Calibration: QueryFactory<GomonTypes.SaveO2CalibrationRequest, GomonTypes.SaveO2CalibrationResponse>;
    nRPlots: QueryFactory<GomonTypes.NRPlotsRequest, GomonTypes.NRPlotsResponse>;
  },
  teraterm: {
    scheduleRaoultCalibration: QueryFactory<GomonTypes.ScheduleRaoultCalibrationRequest, GomonTypes.ScheduleRaoultCalibrationResponse>
    checkTeratermValues: QueryFactory<GomonTypes.CheckTeratermValuesRequest, GomonTypes.CheckTeratermValuesResponse>
    getLatestBatch: QueryFactory<GomonTypes.GetLatestBatchRequest, GomonTypes.GetLatestBatchResponse>
    getBoardsInBatch: QueryFactory<GomonTypes.GetBoardsInBatchRequest, GomonTypes.GetBoardsInBatchResponse>
  },
  rawdata: {
    getDistinctTCMs: QueryFactory<GomonTypes.GetDistinctTCMsRequest, GomonTypes.DevUIDWithTimestamp[]>;
    getDistinctIRs: QueryFactory<GomonTypes.GetDistinctIRsRequest, GomonTypes.IRDevUIDResult[]>;
  },
  sensorAlerts: {
    getByDate: QueryFactory<GomonTypes.SensorAlertsGetByDateRequest, GomonTypes.SensorAlert[]>;
    getByTCMLabel: QueryFactory<GomonTypes.SensorAlertsGetByTCMLabelRequest, GomonTypes.SensorAlert[]>;
    getByFailureTypes: QueryFactory<GomonTypes.SensorAlertsGetByFailureTypesRequest, GomonTypes.SensorAlert[]>;
    listFailureTypes: QueryFactory<void, GomonTypes.FailureType[]>;
    updateTriage: (body: GomonTypes.SensorAlertsUpdateTriageRequest) => Promise<GomonTypes.SensorAlert>;
    delete: (body: GomonTypes.SensorAlertsDeleteRequest) => Promise<void>;
    createFailureType: (body: GomonTypes.SensorAlertsCreateFailureTypeRequest) => Promise<GomonTypes.FailureType>;
    updateFailureType: (body: GomonTypes.SensorAlertsUpdateFailureTypeRequest) => Promise<GomonTypes.FailureType>;
    deleteFailureType: (body: GomonTypes.SensorAlertsDeleteFailureTypeRequest) => Promise<void>;
  },
}

const GomonAPIContext = createContext<GomonAPIContext | null>(null);

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 60 * 60 * 1000 } },
});

export function GomonAPIProvider({ children }: { children: ReactNode }) {
  const { getToken } = useAuth();
  const baseUrl = import.meta.env.VITE_APP_GOMON_API_URL as string;

  const fetchWithAuth = useCallback(
    async <TReq, TRes>(url: string, body: TReq): Promise<TRes> => {
      const token = await getToken();
      const res = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        let message = res.statusText;
        const text = await res.text();
        if (text) {
          try {
            const body = JSON.parse(text) as Record<string, unknown>;
            if (typeof body?.message === "string") message = body.message;
            else if (typeof body?.error === "string") message = body.error;
            else if (typeof body?.detail === "string") message = body.detail;
            else message = text;
          } catch {
            message = text;
          }
        }
        throw new Error(`Failed to fetch ${url}: ${message}`);
      }
      return res.json() as Promise<TRes>;
    },
    [getToken],
  );

  const downloadWithAuth = useCallback(
    async function<T>(url: string, body: T, filename: string): Promise<void> {
      const token = await getToken();
      const res = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        throw new Error(`Failed to fetch ${url}: ${res.statusText}`);
      }
      const obj = window.URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = obj;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(obj);
      a.remove();
    },
    [getToken]
  );

  function queryFactory<TArgs, TData>(url: string): QueryFactory<TArgs, TData> {
    return {
      useSuspenseQuery: (args, opts) =>
        useSuspenseQuery({
          queryKey: [url, args],
          queryFn: () => fetchWithAuth(url, args) as Promise<TData>,
          ...opts,
        }),
      useQuery: (args, opts) =>
        useQuery({
          queryKey: [url, args],
          queryFn: () => fetchWithAuth(url, args) as Promise<TData>,
          ...opts,
        }),
      invalidate: (queryClient, args) => {
        return queryClient.invalidateQueries({ queryKey: [url, args] });
      },
    };
  }

  function downloadFactory<TArgs>(url: string): DownloadFactory<TArgs> {
    return {
      download: (args, filename) => downloadWithAuth(url, args, filename),
    };
  }

  const ctx: GomonAPIContext = useMemo(
    () => ({
      derivedData: {
        getSeries: queryFactory<GomonTypes.GetSeriesRequest, GomonTypes.GetSeriesResponse>(`${baseUrl}/DerivedData/GetSeries`),
        getConsolidated: queryFactory<GomonTypes.GetConsolidatedRequest, GomonTypes.GetConsolidatedResponse>(`${baseUrl}/DerivedData/GetConsolidated`),
        download: downloadFactory<GomonTypes.DownloadRequest>(`${baseUrl}/DerivedData/Download`),
        getRedGreen: queryFactory<GomonTypes.RedGreenRequest, GomonTypes.RedGreenResponse[]>(`${baseUrl}/DerivedData/GetRedGreen`),
      },
      wells: {
        list: queryFactory<GomonTypes.WellsListRequest, GomonTypes.WellsListResponse>(`${baseUrl}/Wells/List`),
        getBySiteId: queryFactory<GomonTypes.WellsGetByIdRequest, GomonTypes.WellInternal[]>(`${baseUrl}/Wells/GetBySiteId`),
        getById: queryFactory<GomonTypes.WellsGetByIdRequest, GomonTypes.WellsGetByIdResponse>(`${baseUrl}/Wells/GetById`),
        edit: (body: GomonTypes.EditWellRequest) => fetchWithAuth(`${baseUrl}/Wells/Edit`, body),
        getDerived: queryFactory<GomonTypes.WellsGetDerivedReq, GomonTypes.WellsGetDerivedResp>(`${baseUrl}/Wells/GetDerived`),
        create: (body: GomonTypes.CreateWellRequest) =>fetchWithAuth(`${baseUrl}/Wells/Create`, body),
      },
      quantifiers: {
        getByWellId: queryFactory<GomonTypes.WellsGetByIdRequest, GomonTypes.Quantifier[]>(`${baseUrl}/Quantifier/GetByWell`),
        getBySiteId: queryFactory<GomonTypes.WellsGetByIdRequest, GomonTypes.Quantifier[]>(`${baseUrl}/Quantifier/GetBySite`),
        create: (body: GomonTypes.CreateQuantifierRequest) => fetchWithAuth(`${baseUrl}/Quantifier/Create`, body),
        edit: (body: GomonTypes.UpdateQuantifierRequest) => fetchWithAuth(`${baseUrl}/Quantifier/Update`, body),
        getAllByTCMDevUID: queryFactory<GomonTypes.GetAllByTCMDevUIDRequest, GomonTypes.Quantifier[]>(`${baseUrl}/Quantifier/GetAllByTCMDevUID`),
      },
      sites: {
        list: queryFactory<GomonTypes.SitesListRequest, GomonTypes.SitesListResponse>(`${baseUrl}/Sites/List`),
        create: (body: GomonTypes.SitesCreateRequest) => fetchWithAuth(`${baseUrl}/Sites/Create`, body),
        update: (body: GomonTypes.SitesUpdateRequest) => fetchWithAuth(`${baseUrl}/Sites/Update`, body),
        getByUserId: queryFactory<GomonTypes.SitesGetByUserIdRequest, GomonTypes.SitesGetByUserIdResponse>(`${baseUrl}/Sites/GetByUserId`),
        getById: queryFactory<GomonTypes.SitesGetBySiteIDRequest, GomonTypes.Site>(`${baseUrl}/Sites/GetById`),
        getDatasetRevisions: queryFactory<GomonTypes.ListDatasetRevisionsBySiteRequest, GomonTypes.DatasetRevision[]>(`${baseUrl}/Sites/ListDatasetRevisionsBySite`),
      },
      getNSZD: queryFactory<GomonTypes.NSZDGetBySiteRequest, GomonTypes.NSZDGetBySiteResponse>(`${baseUrl}/NSZD/GetBySite`),
      getLatestPlumefutures: queryFactory<GomonTypes.PlumeFuturesGetLatestImagesReq, GomonTypes.PlumeFuturesGetLatestImagesResp>(`${baseUrl}/PlumeFutures/GetLatestImages`),
      company: {
        index: queryFactory<GomonTypes.CompaniesIndexRequest, GomonTypes.CompaniesIndexResponse>(`${baseUrl}/companies/Index`),
        show: queryFactory<GomonTypes.CompaniesShowRequest, GomonTypes.CompaniesShowResponse>(`${baseUrl}/companies/Show`),
        listSites: queryFactory<GomonTypes.CompaniesListSitesRequest, GomonTypes.CompaniesListSitesResponse>(`${baseUrl}/companies/ListSites`),
        create: (body: GomonTypes.CompaniesCreateRequest) => fetchWithAuth(`${baseUrl}/companies/Create`, body),
      },
      users: {
        list: queryFactory<GomonTypes.UsersListRequest, GomonTypes.UsersListResponse>(`${baseUrl}/users/Index`),
        get: queryFactory<GomonTypes.UsersGetRequest, GomonTypes.UsersGetResponse>(`${baseUrl}/users/Get`),
        create: (body: GomonTypes.UsersCreateRequest) => fetchWithAuth(`${baseUrl}/users/Create`, body),
        updateUserSites: (body: GomonTypes.UpdateUserSitesRequest) => fetchWithAuth(`${baseUrl}/users/UpdateSites`, body),
        listInternal: queryFactory<GomonTypes.UsersListInternalRequest, GomonTypes.UsersListResponse>(`${baseUrl}/users/ListInternal`),
        setFieldCrew: (body: GomonTypes.SetFieldCrewRequest) => fetchWithAuth(`${baseUrl}/users/SetFieldCrew`, body),
      },
      plans: {
        getDeviceDetails: queryFactory<GomonTypes.GetDeviceDetailsRequest, GomonTypes.GetDeviceDetailsResponse>(`${baseUrl}/plans/GetDeviceDetails`),
        updateDevice: (body: GomonTypes.UpdateDeviceRequest) => fetchWithAuth(`${baseUrl}/plans/UpdateDevice`, body),
      },
      devices: {
        getByLabel: queryFactory<GomonTypes.GetByTCMLabelRequest, GomonTypes.TCM>(`${baseUrl}/Devices/GetByLabel`),
        find: queryFactory<GomonTypes.FindTCMRequest, GomonTypes.TCMResponse>(`${baseUrl}/Devices/FindTCM`),
      },
      gateways: {
        list: queryFactory<GomonTypes.GatewayList, GomonTypes.Gateway[]>(`${baseUrl}/Gateways/List`),
        getNotes: queryFactory<GomonTypes.GetGatewayNotes, GomonTypes.GatewayNotes[]>(`${baseUrl}/Gateways/GetNotes`),
        getWisdmStatus: queryFactory<GomonTypes.GetWisdmStatusRequest, GomonTypes.WisdmGatewayStatus>(`${baseUrl}/Gateways/WisdmStatus`),
        create: (body: GomonTypes.Gateway) => fetchWithAuth(`${baseUrl}/Gateways/Add`, body),
        createNotes: (body: GomonTypes.GatewayNotes) => fetchWithAuth(`${baseUrl}/Gateways/AddNotes`, body),
        update: (body: GomonTypes.Gateway) => fetchWithAuth(`${baseUrl}/Gateways/Update`, body),
      },
      calibration: {
        list: queryFactory<GomonTypes.GetCalibrationRequest, GomonTypes.CalibrationListResponse>(`${baseUrl}/calibration/List`),
        calibrateO2: queryFactory<GomonTypes.CalibrateO2Request, GomonTypes.CalibrateO2Response>(`${baseUrl}/calibration/CalibrateO2`),
        saveO2Calibration: queryFactory<GomonTypes.SaveO2CalibrationRequest, GomonTypes.SaveO2CalibrationResponse>(`${baseUrl}/calibration/SaveO2Calibration`),
        nRPlots: queryFactory<GomonTypes.NRPlotsRequest, GomonTypes.NRPlotsResponse>(`${baseUrl}/calibration/NRPlots`),
      },
      teraterm: {
        scheduleRaoultCalibration: queryFactory<GomonTypes.ScheduleRaoultCalibrationRequest, GomonTypes.ScheduleRaoultCalibrationResponse>(`${baseUrl}/teraterm/ScheduleRaoultCalibration`),
        checkTeratermValues: queryFactory<GomonTypes.CheckTeratermValuesRequest, GomonTypes.CheckTeratermValuesResponse>(`${baseUrl}/teraterm/CheckTeratermValues`),
        getLatestBatch: queryFactory<GomonTypes.GetLatestBatchRequest, GomonTypes.GetLatestBatchResponse>(`${baseUrl}/teraterm/GetLatestBatch`),
        getBoardsInBatch: queryFactory<GomonTypes.GetBoardsInBatchRequest, GomonTypes.GetBoardsInBatchResponse>(`${baseUrl}/teraterm/GetBoardsInBatch`),
      },
      rawdata: {
        getDistinctTCMs: queryFactory<GomonTypes.GetDistinctTCMsRequest, GomonTypes.DevUIDWithTimestamp[]>(`${baseUrl}/RawData/GetDistinctTCMs`),
        getDistinctIRs: queryFactory<GomonTypes.GetDistinctIRsRequest, GomonTypes.IRDevUIDResult[]>(`${baseUrl}/RawData/GetDistinctIRs`),
      },
      sensorAlerts: {
        getByDate: queryFactory<GomonTypes.SensorAlertsGetByDateRequest, GomonTypes.SensorAlert[]>(`${baseUrl}/SensorAlerts/GetSensorAlerts`),
        getByTCMLabel: queryFactory<GomonTypes.SensorAlertsGetByTCMLabelRequest, GomonTypes.SensorAlert[]>(`${baseUrl}/SensorAlerts/GetSensorAlertsByTCMLabel`),
        getByFailureTypes: queryFactory<GomonTypes.SensorAlertsGetByFailureTypesRequest, GomonTypes.SensorAlert[]>(`${baseUrl}/SensorAlerts/GetSensorAlertsByFailureTypes`),
        listFailureTypes: queryFactory<void, GomonTypes.FailureType[]>(`${baseUrl}/SensorAlerts/ListFailureTypes`),
        updateTriage: (body: GomonTypes.SensorAlertsUpdateTriageRequest) => fetchWithAuth(`${baseUrl}/SensorAlerts/UpdateSensorAlertTriage`, body),
        delete: (body: GomonTypes.SensorAlertsDeleteRequest) => fetchWithAuth(`${baseUrl}/SensorAlerts/DeleteSensorAlert`, body),
        createFailureType: (body: GomonTypes.SensorAlertsCreateFailureTypeRequest) => fetchWithAuth(`${baseUrl}/SensorAlerts/CreateFailureType`, body),
        updateFailureType: (body: GomonTypes.SensorAlertsUpdateFailureTypeRequest) => fetchWithAuth(`${baseUrl}/SensorAlerts/UpdateFailureType`, body),
        deleteFailureType: (body: GomonTypes.SensorAlertsDeleteFailureTypeRequest) => fetchWithAuth(`${baseUrl}/SensorAlerts/DeleteFailureType`, body),
      },
    }),
    [baseUrl, fetchWithAuth, downloadWithAuth],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <GomonAPIContext.Provider value={ctx}>{children}</GomonAPIContext.Provider>
    </QueryClientProvider>
  );
}

export function useGomonAPI() {
  const context = useContext(GomonAPIContext);
  if (!context) {
    throw new Error("useGomonAPI must be used within a GomonAPIProvider");
  }
  return context;
}

type FactorySuspenseQueryOptions<T> = Omit<UseSuspenseQueryOptions<T, Error>, "queryKey" | "queryFn">;
type FactoryQueryOptions<T> = Omit<UseQueryOptions<T, Error>, "queryKey" | "queryFn">;

interface QueryFactory<TArgSchema, TData> {
  useSuspenseQuery: (
    args: TArgSchema,
    opts?: FactorySuspenseQueryOptions<TData>,
  ) => UseSuspenseQueryResult<TData, Error>;
  useQuery: (
    args: TArgSchema,
    opts?: FactoryQueryOptions<TData>,
  ) => UseQueryResult<TData, Error>;
  invalidate: (queryClient: QueryClient, args: TArgSchema) => Promise<void>;
}

interface DownloadFactory<TArgSchema> {
  download: (args: TArgSchema, filename: string) => Promise<void>;
}
